import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  isToday,
  isSameMonth,
  isBefore,
  startOfDay,
  addMonths,
  subMonths,
} from 'date-fns';
import { analytics } from '../../lib/analytics';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

interface Member {
  id: string;
  name: string;
  color: string;
}

interface MiniCalendarProps {
  /** Currently selected date (optional) */
  selectedDate?: Date;
  /** Callback when a date is clicked */
  onDateClick?: (date: Date) => void;
  /** Events by date, indexed by date string (yyyy-MM-dd), then by member ID */
  eventsByDate?: Record<string, Record<string, unknown[]>>;
  /** Family members with their colors */
  members?: Member[];
}

/**
 * Compact month calendar widget for the sidebar
 *
 * Features:
 * - Week starts on Sunday
 * - Shows 6 weeks to ensure current date is visually centered
 * - Click navigates to calendar page showing week view of clicked date
 * - Today highlighted with accent color
 * - Days from adjacent months shown in muted style
 * - Optional onDateClick callback for custom behavior
 */
export function MiniCalendar({ selectedDate, onDateClick, eventsByDate }: MiniCalendarProps) {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Today reference for past day detection
  const today = startOfDay(new Date());

  // Generate calendar days (6 weeks to ensure proper centering)
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 }); // Sunday
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  // Auto-navigate to selected date's month when selectedDate changes externally
  // Note: currentMonth intentionally excluded to allow free navigation after selection
  useEffect(() => {
    if (selectedDate) {
      setCurrentMonth(startOfMonth(selectedDate));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const handlePrevMonth = () => {
    const newMonth = subMonths(currentMonth, 1);
    setCurrentMonth(newMonth);
    analytics.miniCalendarMonthChanged('prev', format(newMonth, 'yyyy-MM'));
  };

  const handleNextMonth = () => {
    const newMonth = addMonths(currentMonth, 1);
    setCurrentMonth(newMonth);
    analytics.miniCalendarMonthChanged('next', format(newMonth, 'yyyy-MM'));
  };

  const handleDateClick = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const isPastDay = isBefore(startOfDay(date), today);
    analytics.miniCalendarDateClicked(dateStr, isPastDay);

    if (onDateClick) {
      onDateClick(date);
    } else {
      // Default behavior: navigate to calendar page showing week view of clicked date
      navigate(`/?date=${dateStr}&view=week`);
    }
  };

  const getDayClasses = (day: Date): string => {
    const classes = ['mini-calendar-day'];
    const dayStart = startOfDay(day);

    // Past day detection (before today, but not today itself)
    if (isBefore(dayStart, today)) {
      classes.push('mini-calendar-day--past');
    }

    if (isToday(day)) {
      classes.push('mini-calendar-day--today');
    } else if (!isSameMonth(day, currentMonth)) {
      classes.push('mini-calendar-day--other-month');
    }

    if (selectedDate && isSameDay(day, selectedDate)) {
      classes.push('mini-calendar-day--selected');
    }

    return classes.join(' ');
  };

  // Check if any events exist on a given day (iOS-style single dot indicator)
  const hasEventsOnDay = (day: Date): boolean => {
    if (!eventsByDate) return false;

    const dateKey = format(day, 'yyyy-MM-dd');
    const dayEvents = eventsByDate[dateKey];
    if (!dayEvents) return false;

    // Check if any member has events on this day
    for (const memberId in dayEvents) {
      const memberEvents = dayEvents[memberId];
      if (memberEvents && memberEvents.length > 0) {
        return true;
      }
    }
    return false;
  };

  return (
    <div className="mini-calendar">
      {/* Header with month/year and navigation */}
      <div className="mini-calendar-header">
        <button
          onClick={handlePrevMonth}
          className="mini-calendar-nav lg:min-w-[44px] lg:min-h-[44px]"
          aria-label="Previous month"
        >
          <ChevronLeft className="w-4 h-4 lg:w-5 lg:h-5" />
        </button>
        <span className="mini-calendar-title lg:text-base lg:font-bold">
          {format(currentMonth, 'MMMM yyyy')}
        </span>
        <button
          onClick={handleNextMonth}
          className="mini-calendar-nav lg:min-w-[44px] lg:min-h-[44px]"
          aria-label="Next month"
        >
          <ChevronRight className="w-4 h-4 lg:w-5 lg:h-5" />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="mini-calendar-grid">
        {WEEKDAYS.map((day) => (
          <div key={day} className="mini-calendar-weekday lg:text-sm lg:py-2">
            {day}
          </div>
        ))}

        {/* Calendar days */}
        {days.map((day) => {
          const hasEvents = hasEventsOnDay(day);
          return (
            <button
              key={day.toISOString()}
              onClick={() => handleDateClick(day)}
              className={getDayClasses(day)}
              aria-label={format(day, 'MMMM d, yyyy')}
            >
              <span className="mini-calendar-day-number lg:text-sm lg:font-semibold">{format(day, 'd')}</span>
              <div className="mini-calendar-dots lg:h-2 flex justify-center">
                {hasEvents && (
                  <span className="w-1 h-1 lg:w-1.5 lg:h-1.5 rounded-full bg-teal" />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
